import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ConfirmProvider } from "@/components/ConfirmProvider";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import Login from "@/pages/Login";
import VerifyOtp from "@/pages/VerifyOtp";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import LowStock from "@/pages/LowStock";
import RecentCorrections from "@/pages/RecentCorrections";
import ProductionLog from "@/pages/ProductionLog";
import Materials from "@/pages/Materials";
import MaterialDetail from "@/pages/MaterialDetail";
import Production from "@/pages/Production";
import BomDetail from "@/pages/BomDetail";
import ProductionTimeline from "@/pages/ProductionTimeline";
import Payments from "@/pages/Payments";
import ProductionPlans from "@/pages/ProductionPlans";
import ProductionPlanTimeline from "@/pages/ProductionPlanTimeline";
import Suppliers from "@/pages/Suppliers";
import PurchaseOrders from "@/pages/PurchaseOrders";
import PurchaseOrderTimeline from "@/pages/PurchaseOrderTimeline";
import Rejections from "@/pages/Rejections";
import Dispatch from "@/pages/Dispatch";
import Admins from "@/pages/Admins";
import Chat from "@/pages/Chat";

const NAV_SECTIONS = ["dashboard", "materials", "production", "production-plans", "suppliers", "purchase-orders", "dispatch", "admins"];
const SECTION_PATH = { dashboard: "/", materials: "/materials", production: "/production", "production-plans": "/production-plans", suppliers: "/suppliers", "purchase-orders": "/purchase-orders", dispatch: "/dispatch", admins: "/admins" };

function firstAllowedPath(user) {
  if (!user) return "/login";
  for (const s of NAV_SECTIONS) {
    if (user.is_owner || (user.permissions || {})[s] === "view" || (user.permissions || {})[s] === "edit") return SECTION_PATH[s];
  }
  return null;
}

function AccessDenied() {
  return (
    <div className="p-16 text-center" data-testid="access-denied">
      <h2 className="font-heading font-black text-2xl tracking-tighter text-zinc-900">No Access</h2>
      <p className="text-sm text-zinc-500 mt-2">You don't have permission to view this section. Ask the owner to grant you access.</p>
    </div>
  );
}

function Protected({ children, section }) {
  const { user, hasPerm } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-loading">
        <div className="h-1 w-48 bg-zinc-200 overflow-hidden">
          <div className="h-full w-1/3 bg-primary animate-[loading_1s_infinite]" style={{ animation: "loading 1s linear infinite" }} />
        </div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (section && !hasPerm(section, "view")) {
    const fallback = firstAllowedPath(user);
    if (fallback && fallback !== SECTION_PATH[section]) return <Navigate to={fallback} replace />;
    return <Layout><AccessDenied /></Layout>;
  }
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user && user !== null) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <ConfirmProvider>
            <BrowserRouter>
              <Toaster position="top-right" richColors />
              <PWAInstallPrompt />
              <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/verify" element={<VerifyOtp />} />
            <Route path="/" element={<Protected section="dashboard"><Dashboard /></Protected>} />
            <Route path="/low-stock" element={<Protected section="dashboard"><LowStock /></Protected>} />
            <Route path="/corrections" element={<Protected section="dashboard"><RecentCorrections /></Protected>} />
            <Route path="/production-log" element={<Protected section="dashboard"><ProductionLog /></Protected>} />
            <Route path="/materials" element={<Protected section="materials"><Materials /></Protected>} />
            <Route path="/materials/:id" element={<Protected section="materials"><MaterialDetail /></Protected>} />
            <Route path="/production" element={<Protected section="production"><Production /></Protected>} />
            <Route path="/production/:id/timeline" element={<Protected section="production"><ProductionTimeline /></Protected>} />
            <Route path="/boms/:id" element={<Protected section="production"><BomDetail /></Protected>} />
            <Route path="/production-plans" element={<Protected section="production-plans"><ProductionPlans /></Protected>} />
            <Route path="/production-plans/:id/timeline" element={<Protected section="production-plans"><ProductionPlanTimeline /></Protected>} />
            <Route path="/suppliers" element={<Protected section="suppliers"><Suppliers /></Protected>} />
            <Route path="/purchase-orders" element={<Protected section="purchase-orders"><PurchaseOrders /></Protected>} />
            <Route path="/purchase-orders/:id/timeline" element={<Protected section="purchase-orders"><PurchaseOrderTimeline /></Protected>} />
            <Route path="/rejections" element={<Protected section="dashboard"><Rejections /></Protected>} />
            <Route path="/dispatch" element={<Protected section="dispatch"><Dispatch /></Protected>} />
            <Route path="/payments" element={<Protected section="purchase-orders"><Payments /></Protected>} />
            <Route path="/admins" element={<Protected section="admins"><Admins /></Protected>} />
            <Route path="/chat" element={<Protected><Chat /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </ConfirmProvider>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
